import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useStatusesStandalone } from "../hooks/useStatuses";
import { unifiedProjectsApi, employeesApi } from "../lib/api";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { salesReportGroup } from "../lib/serviceTypeNames";
import {
  Plus,
  Search,
  FolderKanban,
  ChevronRight,
  ChevronDown,
  Briefcase,
  Play,
  TrendingUp,
  DollarSign,
  Filter,
  Archive,
  LayoutList,
  LayoutGrid,
  GripVertical,
  User,
  CheckCircle2,
  ArrowUpRight,
  ArrowDownRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Minus,
  ExternalLink,
  CalendarClock,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import CreateProjectDialog from "../components/CreateProjectDialog";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Phase badges
const PHASE_CONFIG = {
  sales: { label: "Sales", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Briefcase },
  production: { label: "Production", color: "bg-green-500/20 text-green-400 border-green-500/30", icon: Play },
  completed: { label: "Completed", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", icon: Briefcase },
  cancelled: { label: "Cancelled", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: Briefcase },
};

// Clickable table header that toggles sorting on its column.
function SortableHead({ label, sortKey, sortConfig, onSort, align }) {
  const active = sortConfig.key === sortKey;
  return (
    <TableHead
      className={cn(
        "text-zinc-400 cursor-pointer select-none hover:text-zinc-200 transition-colors",
        align === "right" && "text-right"
      )}
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <span className={cn("inline-flex items-center gap-1", align === "right" && "flex-row-reverse")}>
        {label}
        {active ? (
          sortConfig.dir === "asc" ? (
            <ArrowUp className="h-3 w-3 text-orange-400" />
          ) : (
            <ArrowDown className="h-3 w-3 text-orange-400" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </span>
    </TableHead>
  );
}

// Unified status config - uses project.status field (not deal_status)
const STATUS_CONFIG = {
  // Sales phase statuses
  proposal_sent: { label: "Proposal", color: "bg-yellow-500/20 text-yellow-400" },
  contract_negotiation: { label: "Negotiation", color: "bg-blue-500/20 text-blue-400" },
  contract_signed: { label: "Signed", color: "bg-emerald-500/20 text-emerald-400" },
  // PM phase statuses  
  sent_to_pool: { label: "In Pool", color: "bg-purple-500/20 text-purple-400" },
  in_progress: { label: "In Progress", color: "bg-sky-500/20 text-sky-400" },
  awaiting_client: { label: "Awaiting Client", color: "bg-orange-500/20 text-orange-400" },
  awaiting_payment: { label: "Awaiting Payment", color: "bg-amber-500/20 text-amber-400" },
  completed: { label: "Completed", color: "bg-green-500/20 text-green-400" },
  // Legacy
  active: { label: "Active", color: "bg-sky-500/20 text-sky-400" },
  draft: { label: "Draft", color: "bg-zinc-500/20 text-zinc-400" },
};

// Statuses where we surface "how long stuck here" hint under the badge —
// these are the two states where the project is blocked waiting on the client.
const STATUSES_WITH_DURATION = new Set(["awaiting_client", "awaiting_payment"]);

const formatStatusDuration = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const diffMs = Date.now() - t;
  if (diffMs < 60_000) return "just now";
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) {
    const remH = hours - days * 24;
    return days < 7 && remH > 0 ? `${days}d ${remH}h` : `${days}d`;
  }
  if (hours >= 1) return `${hours}h`;
  return `${minutes}m`;
};

// Older = more urgent. Used by both table row and kanban card so colors stay consistent.
const statusDurationToneClass = (iso) => {
  if (!iso) return "text-zinc-500";
  const diffDays = (Date.now() - Date.parse(iso)) / 86_400_000;
  if (diffDays >= 7) return "text-red-400";
  if (diffDays >= 3) return "text-amber-400";
  return "text-zinc-400";
};

const KANBAN_COLUMNS = [
  { id: "proposal_sent", label: "Proposal", color: "border-t-yellow-500" },
  { id: "contract_negotiation", label: "Negotiation", color: "border-t-blue-500" },
  { id: "contract_signed", label: "Signed", color: "border-t-emerald-500" },
  { id: "sent_to_pool", label: "In Pool", color: "border-t-purple-500" },
  { id: "in_progress", label: "In Progress", color: "border-t-sky-500" },
  { id: "awaiting_client", label: "Awaiting Client", color: "border-t-orange-500" },
  { id: "awaiting_payment", label: "Awaiting Payment", color: "border-t-amber-500" },
];

// Draggable project card for Kanban
const KanbanCard = ({ project, navigate, getCurrencySymbol, hideFinancials = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: project.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const statusCfg = STATUS_CONFIG[project.status] || {};

  return (
    <div ref={setNodeRef} style={style} {...attributes}
      className={cn("bg-zinc-800/60 rounded-md p-2.5 border border-zinc-700/50 hover:border-zinc-600 cursor-pointer group transition-colors", isDragging && "ring-1 ring-orange-500/50")}
      data-testid={`kanban-card-${project.id}`}
    >
      <div className="flex items-start gap-1.5">
        <div {...listeners} className="mt-0.5 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0">
          <GripVertical className="h-3.5 w-3.5 text-zinc-500" />
        </div>
        <div className="min-w-0 flex-1" onClick={() => navigate(`/projects/${project.id}`)}>
          <p className="text-zinc-50 text-xs font-medium truncate leading-tight">{project.name}</p>
          <p className="text-zinc-500 text-[10px] truncate">{project.client_name || "—"}</p>
        </div>
      </div>
      {STATUSES_WITH_DURATION.has(project.status) && project.status_since && (
        <div
          className={cn("mt-1 text-[10px] font-mono leading-tight", statusDurationToneClass(project.status_since))}
          title={`In this status since ${new Date(project.status_since).toLocaleString()}`}
        >
          {formatStatusDuration(project.status_since)} here
        </div>
      )}
      <div className="flex items-center justify-between mt-1.5 gap-1">
        {!hideFinancials && project.total_revenue > 0 && (
          <span className="text-emerald-400 text-[10px] font-mono">{getCurrencySymbol("USD")}{Math.round(project.total_revenue).toLocaleString()}</span>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          {project.art_director_name && (
            <span
              className="text-purple-300/80 text-[10px] flex items-center gap-0.5"
              title={`Art Director: ${project.art_director_name}`}
            >
              <User className="h-2.5 w-2.5" />{project.art_director_name?.split(' ')[0]}
            </span>
          )}
          {project.project_manager_name && (
            <span
              className="text-zinc-500 text-[10px] flex items-center gap-0.5"
              title={`PM: ${project.project_manager_name}`}
            >
              <User className="h-2.5 w-2.5" />{project.project_manager_name?.split(' ')[0]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Fully-loaded cost breakdown — the waterfall of where a project's margin goes.
// All values are USD (the loaded-cost model is computed in USD on the backend).
const CostBreakdown = ({ cb }) => {
  const usd = (v) => `$${Math.round(v || 0).toLocaleString()}`;
  const rev = cb.revenue || 0;
  const pct = (v) => (rev > 0 ? `${((v / rev) * 100).toFixed(0)}%` : "—");

  // Stacked bar segments (cost lines + remaining net profit), proportional to revenue.
  const segs = [
    { key: "artist", label: "Artist", v: cb.artist, color: "bg-sky-500" },
    { key: "production", label: "Production", v: cb.production, color: "bg-cyan-500" },
    { key: "art_director", label: "Art Director", v: cb.art_director, color: "bg-indigo-500" },
    { key: "pm_salary", label: "PM salary", v: cb.pm_salary, color: "bg-amber-500" },
    { key: "sales_salary", label: "Sales salary", v: cb.sales_salary, color: "bg-orange-500" },
    { key: "sales_commission", label: "Sales commission", v: cb.sales_commission, color: "bg-rose-500" },
    { key: "pm_bonus", label: "PM bonus", v: cb.pm_bonus, color: "bg-yellow-600" },
    { key: "overhead", label: "Overhead", v: cb.overhead, color: "bg-fuchsia-600" },
    { key: "development", label: "Development", v: cb.development, color: "bg-teal-500" },
    { key: "payroll_tax", label: "Payroll tax", v: cb.payroll_tax, color: "bg-red-500" },
    { key: "revenue_tax", label: "Revenue tax", v: cb.revenue_tax, color: "bg-red-700" },
    { key: "other_expenses", label: "Other expenses", v: cb.other_expenses, color: "bg-stone-500" },
  ].filter((s) => (s.v || 0) > 0);
  const netPositive = (cb.net_profit || 0) > 0;

  const Line = ({ label, value, color, strong, tone, tip }) => {
    const labelEl = (
      <span className={cn("truncate", strong ? "text-zinc-200 font-medium" : "text-zinc-400", tip && "underline decoration-dotted decoration-zinc-600 underline-offset-2 cursor-help")}>
        {label}
      </span>
    );
    return (
      <div className="flex items-center justify-between gap-3 py-0.5">
        <span className="flex items-center gap-1.5 min-w-0">
          {color && <span className={cn("inline-block h-2 w-2 rounded-sm flex-shrink-0", color)} />}
          {tip ? (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>{labelEl}</TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="bg-zinc-950 border-zinc-700 text-xs max-w-[320px] p-2.5">
                  {tip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : labelEl}
        </span>
        <span className="flex items-center gap-2 font-mono flex-shrink-0">
          <span className={cn(tone || (strong ? "text-zinc-200" : "text-zinc-300"))}>{usd(value)}</span>
          <span className="text-zinc-600 w-9 text-right">{pct(value)}</span>
        </span>
      </div>
    );
  };

  // Hover derivations for the Indirect — allocated labour lines.
  const calc = cb.calc || {};
  const monthRows = (c) => {
    const rows = c?.rows || [];
    const shown = rows.slice(0, 8);
    return (
      <>
        {shown.map((r, i) => (
          <div key={i} className="flex justify-between gap-3 font-mono text-zinc-300">
            <span className="text-zinc-500">{r[0]}</span>
            <span className="text-zinc-500">÷ {r[1]} proj</span>
            <span>{usd(r[2])}</span>
          </div>
        ))}
        {rows.length > shown.length && <div className="text-zinc-500">+{rows.length - shown.length} more months…</div>}
      </>
    );
  };
  const salaryTip = (c, withNetout) => {
    if (!c) return <span className="text-zinc-400">No allocation (unassigned or no salary on record).</span>;
    return (
      <div className="space-y-1">
        <div className="font-medium text-zinc-100">{c.name || "—"} · {usd(c.salary_usd)}/mo</div>
        <div className="text-[10px] text-zinc-500">monthly salary ÷ concurrent active projects, summed per active month</div>
        <div className="space-y-0.5 border-t border-zinc-800 pt-1">{monthRows(c)}</div>
        {withNetout && (c.ws_netout || 0) > 0 && (
          <div className="flex justify-between font-mono text-zinc-400"><span>− own Worksection hours</span><span>−{usd(c.ws_netout)}</span></div>
        )}
        <div className="flex justify-between font-mono font-medium text-zinc-100 border-t border-zinc-700 pt-1">
          <span>= allocated</span><span>{usd(c.total)}</span>
        </div>
      </div>
    );
  };
  const commissionTip = (() => {
    const c = calc.sales_commission || {};
    if (c.note) return <span className="text-zinc-300">{c.note}.</span>;
    return (
      <div className="font-mono text-zinc-300">
        {c.rate_pct}% × {usd(c.base)} revenue = <span className="text-zinc-100 font-medium">{usd(cb.sales_commission)}</span>
      </div>
    );
  })();
  const pmBonusTip = (() => {
    const c = calc.pm_bonus || {};
    if ((c.base || 0) <= 0) return <span className="text-zinc-300">Direct profit ≤ 0 → no bonus.</span>;
    return (
      <div className="font-mono text-zinc-300">
        {c.rate_pct}% × {usd(c.base)} direct profit = <span className="text-zinc-100 font-medium">{usd(cb.pm_bonus)}</span>
      </div>
    );
  })();
  const overheadTip = (() => {
    const c = calc.overhead || {};
    return (
      <div className="text-zinc-300 space-y-1">
        <div>Share of <span className="font-mono text-zinc-100">{usd(c.pool_monthly)}/mo</span> company overhead pool (non-PM/sales/production salaried staff, e.g. finance/admin).</div>
        <div className="text-[10px] text-zinc-500">Spread across active projects by revenue share, over {c.active_months} active month{c.active_months === 1 ? "" : "s"} → <span className="font-mono text-zinc-300">{usd(c.total)}</span>.</div>
      </div>
    );
  })();
  const developmentTip = (() => {
    const c = calc.development || {};
    return (
      <div className="text-zinc-300 space-y-1">
        <div>Dev team that builds Spinhouse/software: <span className="font-mono text-zinc-100">{usd(c.pool_monthly)}/mo</span>{c.dev_pct != null && c.dev_pct < 100 ? <span className="text-zinc-500"> ({c.dev_pct}% of dev payroll)</span> : null}.</div>
        <div className="text-[10px] text-zinc-500">Charged to software projects only, by revenue share, over {c.active_months} active month{c.active_months === 1 ? "" : "s"} → <span className="font-mono text-zinc-300">{usd(c.total)}</span>.</div>
      </div>
    );
  })();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-xs">
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">Where the margin goes (USD)</span>
        <span className="font-mono text-zinc-300">Revenue base <span className="text-zinc-50 font-medium">{usd(rev)}</span></span>
      </div>
      {/* Proportional stacked bar */}
      <div className="flex h-2.5 w-full overflow-hidden rounded-sm bg-zinc-800">
        {segs.map((s) => (
          <div key={s.key} className={s.color} style={{ width: `${rev > 0 ? (s.v / rev) * 100 : 0}%` }} title={`${s.label}: ${usd(s.v)}`} />
        ))}
        {netPositive && (
          <div className="bg-emerald-500" style={{ width: `${rev > 0 ? (cb.net_profit / rev) * 100 : 0}%` }} title={`Net profit: ${usd(cb.net_profit)}`} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-1 text-xs">
        {/* Direct costs */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Direct costs</div>
          <Line label="Artist (freelance)" value={cb.artist} color="bg-sky-500" />
          <Line label="Production (staff/WS)" value={cb.production} color="bg-cyan-500" />
          <Line label="Art Director fee" value={cb.art_director} color="bg-indigo-500" />
          <div className="border-t border-zinc-800 mt-1 pt-1">
            <Line label="Direct profit" value={cb.direct_profit} strong tone="text-zinc-300" />
            <div className="text-[10px] text-zinc-500 text-right">gross margin {cb.direct_margin}%</div>
          </div>
        </div>

        {/* Indirect (allocated) labour */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Indirect — allocated labour</div>
          <Line label="PM salary" value={cb.pm_salary} color="bg-amber-500" tip={salaryTip(calc.pm_salary, true)} />
          <Line label="Sales salary" value={cb.sales_salary} color="bg-orange-500" tip={salaryTip(calc.sales_salary, false)} />
          <Line label="Sales commission" value={cb.sales_commission} color="bg-rose-500" tip={commissionTip} />
          <Line label="PM bonus" value={cb.pm_bonus} color="bg-yellow-600" tip={pmBonusTip} />
          <Line label="Company overhead" value={cb.overhead} color="bg-fuchsia-600" tip={overheadTip} />
          {(cb.development || 0) > 0 && (
            <Line label="Development (Spinhouse)" value={cb.development} color="bg-teal-500" tip={developmentTip} />
          )}
        </div>

        {/* Taxes & other */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Taxes &amp; other</div>
          <Line label="Payroll tax" value={cb.payroll_tax} color="bg-red-500" />
          <Line label="Revenue tax (FOP/LLC)" value={cb.revenue_tax} color="bg-red-700" />
          <Line label="Other expenses" value={cb.other_expenses} color="bg-stone-500" />
          {cb.capped && (
            <Line
              label="− Uncharged idle"
              value={-cb.uncharged_idle}
              tone="text-amber-400"
              tip={
                <div className="text-zinc-300 space-y-1">
                  <div>Allocated indirect (<span className="font-mono">{usd(cb.indirect_uncapped)}</span>) exceeded the cap of {rev > 0 ? "100%" : ""} of revenue.</div>
                  <div className="text-[10px] text-zinc-500">This project sat open longer than it was actively worked (e.g. waiting on payment). The excess <span className="font-mono text-amber-400">{usd(cb.uncharged_idle)}</span> is idle/waiting capacity — treated as a company period-cost, not charged here.</div>
                </div>
              }
            />
          )}
          <div className="border-t border-zinc-800 mt-1 pt-1">
            <Line label="Total indirect" value={cb.indirect_total} strong tone="text-zinc-300" />
          </div>
        </div>
      </div>

      {/* Bottom line */}
      <div className="flex items-center justify-between border-t border-zinc-700 pt-2 text-sm">
        <span className="text-zinc-400">Net profit (loaded)</span>
        <span className="flex items-center gap-2 font-mono">
          <span className={cn("font-semibold", (cb.net_profit || 0) >= 0 ? "text-emerald-400" : "text-red-400")}>{usd(cb.net_profit)}</span>
          <span className={cn("w-12 text-right font-semibold", (cb.net_margin || 0) >= 30 ? "text-emerald-400" : "text-red-400")}>{cb.net_margin}%</span>
        </span>
      </div>
      <p className="text-[10px] text-zinc-600 leading-snug">
        Indirect labour is capacity-allocated (salary ÷ concurrent active projects × project months), not time-tracked — treat as an estimate. Dev-team cost is charged to software (Spinhouse) projects only. Indirect is capped at a % of revenue so idle/waiting time on long-open projects isn't dumped on the project (the excess is a company period-cost).
      </p>
    </div>
  );
};

const UnifiedProjectsPage = () => {
  const { user } = useAuth();
  // Art directors see project list without pricing/financial columns.
  // ad_qc_marker_art_director_scrub_v1
  const isArtDirector = user?.role === "art_director";
  const { formatAmount, convertAmount, displayCurrency, getCurrencySymbol } = useCurrency();
  const { projectStatuses, getProjectStatus } = useStatusesStandalone();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [managers, setManagers] = useState([]); // PM and Sales managers
  const [artDirectors, setArtDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pmFilter, setPmFilter] = useState("all");
  const [salesFilter, setSalesFilter] = useState("all");
  const [adFilter, setAdFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");
  const [marginRange, setMarginRange] = useState([0, 100]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewTab, setViewTab] = useState("active"); // "active" or "archive"
  const [viewMode, setViewMode] = useState("table"); // "table" or "kanban"
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" }); // table column sort
  const [breakdownOpen, setBreakdownOpen] = useState(null); // project id whose fully-loaded cost breakdown is expanded
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Art Directors come from db.staff (role="art_director"), not from
      // db.employees — that's why we hit /users separately rather than
      // filtering the employees list.
      const [projectsRes, employeesRes, adsRes] = await Promise.all([
        unifiedProjectsApi.getAll(),
        employeesApi.getAll(),
        api.get("/users?role=art_director").catch(() => ({ data: [] })),
      ]);
      setProjects(projectsRes.data || []);
      // Get managers from employees
      const emps = employeesRes.data || [];
      setManagers(emps.filter(e => e.is_active && ["project_manager", "sales_manager", "admin"].includes(e.position)));
      setArtDirectors(adsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  // Stats - with proper currency conversion
  const stats = useMemo(() => {
    const salesProjects = projects.filter(p => p.phase === "sales");
    const productionProjects = projects.filter(p => p.phase === "production");
    
    // Convert all amounts to UAH for proper calculation
    const totalPipeline = salesProjects.reduce((sum, p) => {
      const amount = p.total_amount || 0;
      const amountInUAH = convertAmount(amount, p.currency || "USD");
      return sum + (amountInUAH * (p.probability || 0) / 100);
    }, 0);
    
    const totalProduction = productionProjects.reduce((sum, p) => {
      const amount = p.total_amount || 0;
      const amountInUAH = convertAmount(amount, p.currency || "USD");
      return sum + amountInUAH;
    }, 0);
    
    return {
      total: projects.length,
      sales: salesProjects.length,
      production: productionProjects.length,
      completed: projects.filter(p => p.phase === "completed").length,
      pipeline: totalPipeline,
      productionValue: totalProduction,
    };
  }, [projects, convertAmount, displayCurrency]);

  // Unique statuses for filter
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(projects.map(p => p.status).filter(Boolean));
    return Array.from(statuses);
  }, [projects]);

  // PM and Sales managers for filters
  const pmManagers = useMemo(() => managers.filter(m => m.position === "project_manager" || m.position === "admin"), [managers]);
  // Sales options are derived from the projects themselves (like uniqueClients):
  // salespeople assigned on projects don't reliably carry position="sales_manager"
  // in the employees collection, so the position-based list came back empty.
  const salesManagers = useMemo(() => {
    const sales = new Map();
    projects.forEach(p => {
      if (p.sales_manager_id && p.sales_manager_name) {
        sales.set(p.sales_manager_id, p.sales_manager_name);
      }
    });
    return Array.from(sales.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // Unique clients for filter
  const uniqueClients = useMemo(() => {
    const clients = new Map();
    projects.forEach(p => {
      if (p.client_id && p.client_name) {
        clients.set(p.client_id, p.client_name);
      }
    });
    return Array.from(clients.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // Unique service-type groups for the filter. Raw per-service values come from
  // the backend (project.service_types); salesReportGroup collapses Spinhouse
  // sub-types into one "Spinhouse" product, matching /sales-pnl.
  const uniqueServiceTypes = useMemo(() => {
    const groups = new Set();
    projects.forEach(p => {
      (p.service_types || []).forEach(st => {
        const g = salesReportGroup(st);
        if (g) groups.add(g);
      });
    });
    return Array.from(groups).sort((a, b) => a.localeCompare(b));
  }, [projects]);

  // Filtered projects (with archive tab support)
  const ARCHIVE_STATUSES = ["completed", "archived"];
  
  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      // Tab filter: active = not completed/archived, archive = completed/archived
      const isArchived = ARCHIVE_STATUSES.includes(p.status) || p.phase === "completed";
      if (viewTab === "active" && isArchived) return false;
      if (viewTab === "archive" && !isArchived) return false;
      
      const matchesSearch = !searchQuery || 
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPhase = phaseFilter === "all" || p.phase === phaseFilter;
      const matchesStatus = statusFilter === "all" || p.status === statusFilter;
      const matchesPM = pmFilter === "all" || p.project_manager_id === pmFilter;
      const matchesSales = salesFilter === "all" || p.sales_manager_id === salesFilter;
      const matchesAD = adFilter === "all"
        || (adFilter === "__unset__" ? !p.art_director_id : p.art_director_id === adFilter);
      const matchesClient = clientFilter === "all" || p.client_id === clientFilter;
      const matchesServiceType = serviceTypeFilter === "all"
        || (p.service_types || []).some(st => salesReportGroup(st) === serviceTypeFilter);

      // Margin filter (slider 0-100, full range = no filter)
      // Projects without computable margin ("—" in the table — no revenue or no costs entered)
      // are treated as "unknown / effectively zero" and shown whenever the lower bound is 0.
      let matchesMargin = true;
      const [mMin, mMax] = marginRange;
      const marginActive = mMin > 0 || mMax < 100;
      if (marginActive) {
        const revenue = p.total_amount || 0;
        const costs = p.total_costs || 0;
        const hasCosts = costs > 0;
        const hasRevenue = revenue > 0;
        if (!hasRevenue || !hasCosts) {
          matchesMargin = mMin === 0;
        } else {
          const marginPct = ((revenue - costs) / revenue) * 100;
          matchesMargin = marginPct >= mMin && marginPct <= mMax;
        }
      }

      return matchesSearch && matchesPhase && matchesStatus && matchesPM && matchesSales && matchesAD && matchesClient && matchesServiceType && matchesMargin;
    });
  }, [projects, searchQuery, phaseFilter, statusFilter, pmFilter, salesFilter, adFilter, clientFilter, serviceTypeFilter, marginRange, viewTab]);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  }, []);

  // Apply column sort to the filtered table rows. Empty/unknown values sort
  // last regardless of direction so blanks never bubble to the top.
  const sortedProjects = useMemo(() => {
    if (!sortConfig.key) return filteredProjects;
    const accessors = {
      name: (p) => p.name || "",
      client_name: (p) => p.client_name || "",
      project_manager_name: (p) => p.project_manager_name || "",
      sales_manager_name: (p) => p.sales_manager_name || "",
      art_director_name: (p) => p.art_director_name || "",
      status: (p) => getProjectStatus(p.status)?.name || p.status || "",
      deadline: (p) => p.deadline || "",
      total_amount: (p) => p.total_amount || 0,
      total_costs: (p) => p.total_costs || 0,
      margin: (p) => {
        const r = p.total_amount || 0;
        const c = p.total_costs || 0;
        return r > 0 && c > 0 ? ((r - c) / r) * 100 : null;
      },
      loaded_margin: (p) => (p.cost_breakdown && (p.total_amount || 0) > 0 ? (p.loaded_margin ?? null) : null),
    };
    const acc = accessors[sortConfig.key];
    if (!acc) return filteredProjects;
    const dir = sortConfig.dir === "asc" ? 1 : -1;
    return [...filteredProjects].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      const aEmpty = va === null || va === "";
      const bEmpty = vb === null || vb === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
  }, [filteredProjects, sortConfig, getProjectStatus]);
  
  const activeCount = useMemo(() => projects.filter(p => !ARCHIVE_STATUSES.includes(p.status) && p.phase !== "completed").length, [projects]);
  const archiveCount = useMemo(() => projects.filter(p => ARCHIVE_STATUSES.includes(p.status) || p.phase === "completed").length, [projects]);

  // Kanban: group active projects by status
  const kanbanData = useMemo(() => {
    const active = projects.filter(p => !ARCHIVE_STATUSES.includes(p.status) && p.phase !== "completed");
    const grouped = {};
    KANBAN_COLUMNS.forEach(col => { grouped[col.id] = []; });
    active.forEach(p => {
      const status = p.status || "proposal_sent";
      if (grouped[status]) grouped[status].push(p);
      else if (grouped["in_progress"]) grouped["in_progress"].push(p); // fallback
    });
    return grouped;
  }, [projects]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || !active) return;
    
    const projectId = active.id;
    // Find which column the card was dropped onto
    const targetColumn = over.id;
    // Check if dropped on a column header or another card
    const targetStatus = KANBAN_COLUMNS.find(c => c.id === targetColumn)?.id 
      || KANBAN_COLUMNS.find(c => kanbanData[c.id]?.some(p => p.id === over.id))?.id;
    
    if (!targetStatus) return;
    
    // Find the project and check if status actually changed
    const project = projects.find(p => p.id === projectId);
    if (!project || project.status === targetStatus) return;
    
    try {
      await unifiedProjectsApi.update(projectId, { status: targetStatus });
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, status: targetStatus } : p));
      toast.success(`${project.name} → ${KANBAN_COLUMNS.find(c => c.id === targetStatus)?.label}`);
    } catch (err) {
      toast.error("Failed to update status");
    }
  }, [projects, kanbanData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="unified-projects-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Projects</h1>
          <p className="text-zinc-400 text-sm">Unified view of all projects from sales to delivery</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center bg-zinc-900 rounded-md border border-zinc-800 p-0.5" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode("table")}
              className={cn("p-1.5 rounded transition-colors", viewMode === "table" ? "bg-zinc-700 text-zinc-50" : "text-zinc-500 hover:text-zinc-300")}
              data-testid="view-mode-table"
              title="Table view"
            >
              <LayoutList className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("kanban")}
              className={cn("p-1.5 rounded transition-colors", viewMode === "kanban" ? "bg-zinc-700 text-zinc-50" : "text-zinc-500 hover:text-zinc-300")}
              data-testid="view-mode-kanban"
              title="Kanban view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
          
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-orange-500 hover:bg-orange-600" 
            data-testid="create-project-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>
      </div>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={fetchData}
        defaultPhase="sales"
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Minimal Active/Archive toggle */}
        <div className="flex items-center gap-0.5 mr-2 border-b border-zinc-800" data-testid="project-view-tabs">
          <button
            onClick={() => setViewTab("active")}
            className={cn("px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px",
              viewTab === "active" ? "text-orange-400 border-orange-400" : "text-zinc-500 border-transparent hover:text-zinc-300")}
            data-testid="tab-active-projects"
          >
            Active {activeCount}
          </button>
          <button
            onClick={() => setViewTab("archive")}
            className={cn("px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px",
              viewTab === "archive" ? "text-zinc-300 border-zinc-400" : "text-zinc-500 border-transparent hover:text-zinc-300")}
            data-testid="tab-archive-projects"
          >
            Archive {archiveCount}
          </button>
        </div>
        
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-800 text-zinc-50 h-9"
            data-testid="search-input"
          />
        </div>
        
        <Select value={phaseFilter} onValueChange={setPhaseFilter}>
          <SelectTrigger className="w-[140px] bg-zinc-900/50 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Phase" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Phases</SelectItem>
            <SelectItem value="sales">Sales</SelectItem>
            <SelectItem value="production">Production</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] bg-zinc-900/50 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Statuses</SelectItem>
            {uniqueStatuses.map(s => (
              <SelectItem key={s} value={s}>{getProjectStatus(s)?.name || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={pmFilter} onValueChange={setPmFilter}>
          <SelectTrigger className="w-[150px] bg-zinc-900/50 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="PM" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All PMs</SelectItem>
            {pmManagers.map(m => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={salesFilter} onValueChange={setSalesFilter}>
          <SelectTrigger className="w-[150px] bg-zinc-900/50 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Sales" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Sales</SelectItem>
            {salesManagers.map(m => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={adFilter} onValueChange={setAdFilter}>
          <SelectTrigger className="w-[170px] bg-zinc-900/50 border-zinc-800 text-zinc-50 h-9" data-testid="filter-art-director">
            <SelectValue placeholder="Art Director" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
            <SelectItem value="all">All Art Directors</SelectItem>
            <SelectItem value="__unset__" className="text-zinc-400 italic">— Not assigned —</SelectItem>
            {artDirectors.map(ad => (
              <SelectItem key={ad.id} value={ad.id}>{ad.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[180px] bg-zinc-900/50 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Client" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
            <SelectItem value="all">All Clients</SelectItem>
            {uniqueClients.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
          <SelectTrigger className="w-[180px] bg-zinc-900/50 border-zinc-800 text-zinc-50 h-9" data-testid="filter-service-type">
            <SelectValue placeholder="Service" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
            <SelectItem value="all">All Services</SelectItem>
            {uniqueServiceTypes.map(st => (
              <SelectItem key={st} value={st}>{st}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div
          className="flex items-center gap-2 px-3 h-9 bg-zinc-900/50 border border-zinc-800 rounded-md min-w-[220px]"
          data-testid="filter-margin"
          title="Filter projects by margin %"
        >
          <span className="text-zinc-500 text-xs whitespace-nowrap">Margin</span>
          <Slider
            min={0}
            max={100}
            step={1}
            value={marginRange}
            onValueChange={setMarginRange}
            className="flex-1"
          />
          <span className="text-zinc-300 text-xs font-mono tabular-nums whitespace-nowrap min-w-[58px] text-right">
            {marginRange[0]}–{marginRange[1]}%
          </span>
          {(marginRange[0] > 0 || marginRange[1] < 100) && (
            <button
              type="button"
              onClick={() => setMarginRange([0, 100])}
              className="text-zinc-500 hover:text-zinc-300 text-xs"
              data-testid="filter-margin-reset"
              title="Reset margin filter"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Table View */}
      {viewMode === "table" && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <SortableHead label="Project" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Client" sortKey="client_name" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="PM" sortKey="project_manager_name" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Sales" sortKey="sales_manager_name" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Art Director" sortKey="art_director_name" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Deadline" sortKey="deadline" sortConfig={sortConfig} onSort={handleSort} />
                  {!isArtDirector && (
                    <>
                      <SortableHead label="Revenue" sortKey="total_amount" sortConfig={sortConfig} onSort={handleSort} align="right" />
                      <SortableHead label="Costs" sortKey="total_costs" sortConfig={sortConfig} onSort={handleSort} align="right" />
                      <SortableHead label="Margin" sortKey="margin" sortConfig={sortConfig} onSort={handleSort} align="right" />
                      <SortableHead label="Net" sortKey="loaded_margin" sortConfig={sortConfig} onSort={handleSort} align="right" />
                    </>
                  )}
                  <TableHead className="text-zinc-400"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isArtDirector ? 8 : 12} className="text-center text-zinc-500 py-8">
                      No projects found
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedProjects.map((project) => {
                    const phaseConfig = PHASE_CONFIG[project.phase] || PHASE_CONFIG.sales;
                    const statusConfig = getProjectStatus(project.status);
                    const projectUrl = `/sales/project/${project.id}`;
                    const projectCurrency = project.currency || "USD";
                    const revenue = project.total_amount || 0;
                    const costs = project.total_costs || 0;
                    const profit = revenue - costs;
                    const hasCosts = costs > 0;
                    const marginPercent = revenue > 0 && hasCosts ? (profit / revenue) * 100 : 0;
                    const marginColor = !hasCosts ? "text-amber-400" :
                      marginPercent >= 50 ? "text-green-400" :
                      marginPercent >= 30 ? "text-yellow-400" : "text-red-400";

                    // Fully-loaded margin (full absorption: + PM/sales salary, commission, PM bonus, overhead)
                    // Net% is visible to all; the detailed breakdown (cb) is only sent to
                    // admin / sales_director / COO (pm_lead) — others can't expand the row.
                    const cb = project.cost_breakdown || null;
                    const loadedProfit = project.loaded_profit;
                    const loadedMargin = project.loaded_margin;
                    const hasLoaded = revenue > 0 && hasCosts && loadedMargin != null;
                    const canExpand = !!cb;
                    const loadedColor = !hasLoaded ? "text-zinc-500" :
                      loadedMargin >= 50 ? "text-green-400" :
                      loadedMargin >= 30 ? "text-yellow-400" : "text-red-400";
                    const isBreakdownOpen = breakdownOpen === project.id;

                    const pv = project.payment_validation;
                    
                    return (
                      <React.Fragment key={project.id}>
                      <TableRow
                        className="border-zinc-800 hover:bg-zinc-800/30"
                        data-testid={`project-row-${project.id}`}
                      >
                        <TableCell>
                          <Link to={projectUrl} className="flex items-center gap-2 group" data-testid={`project-link-${project.id}`}>
                            <div className="p-1.5 bg-zinc-800 rounded-lg">
                              <FolderKanban className="h-3.5 w-3.5 text-orange-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-zinc-50 text-sm group-hover:text-orange-400 transition-colors">{project.name}</p>
                              <p className="text-[10px] text-zinc-500">
                                {phaseConfig.label} • {project.task_count || 0} tasks
                              </p>
                            </div>
                            <ExternalLink className="h-3 w-3 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity ml-1 flex-shrink-0" />
                          </Link>
                        </TableCell>
                        <TableCell className="text-zinc-300 text-sm">{project.client_name || "-"}</TableCell>
                        <TableCell><span className="text-zinc-400 text-sm">{project.project_manager_name || "-"}</span></TableCell>
                        <TableCell><span className="text-zinc-400 text-sm">{project.sales_manager_name || "-"}</span></TableCell>
                        <TableCell>
                          {project.art_director_name ? (
                            <span className="text-purple-300 text-sm">{project.art_director_name}</span>
                          ) : (
                            <span className="text-zinc-600 text-sm italic">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-start gap-0.5">
                            <Badge variant="outline" className={cn("text-xs", statusConfig.color)}>
                              {statusConfig.name}
                            </Badge>
                            {STATUSES_WITH_DURATION.has(project.status) && project.status_since && (
                              <span
                                className={cn("text-[10px] font-mono leading-tight", statusDurationToneClass(project.status_since))}
                                title={`In this status since ${new Date(project.status_since).toLocaleString()}`}
                                data-testid={`status-duration-${project.id}`}
                              >
                                {formatStatusDuration(project.status_since)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {project.deadline ? (() => {
                            const isActive = !ARCHIVE_STATUSES.includes(project.status) && project.phase !== "completed";
                            const overdue = isActive && project.deadline < new Date().toISOString().slice(0, 10);
                            return (
                              <span className={cn("inline-flex items-center gap-1 text-xs font-mono", overdue ? "text-red-400" : "text-zinc-400")}>
                                <CalendarClock className="h-3 w-3" />
                                {project.deadline}
                              </span>
                            );
                          })() : (
                            <span className="text-zinc-600 text-sm italic">—</span>
                          )}
                        </TableCell>
                        {!isArtDirector && (
                          <>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <span className="text-zinc-50 font-mono text-sm">{formatAmount(revenue, projectCurrency)}</span>
                                {pv && pv.status !== "empty" && (
                                  <TooltipProvider delayDuration={100}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex cursor-help" data-testid={`validation-icon-${project.id}`}>
                                          {pv.status === "match" ? (
                                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                          ) : pv.direction === "over" ? (
                                            <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" />
                                          ) : (
                                            <ArrowDownRight className="h-3.5 w-3.5 text-red-500" />
                                          )}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="left" className="bg-zinc-900 border-zinc-700 text-xs max-w-[240px]">
                                        {pv.status === "match" ? (
                                          <p className="text-emerald-400">
                                            Платежи = {pv.source === "requests" ? "реквесты" : "задачи"}
                                          </p>
                                        ) : (
                                          <div>
                                            <p className="text-zinc-300 mb-1">
                                              Платежи vs {pv.source === "requests" ? "реквесты" : "задачи"}
                                            </p>
                                            <p className={pv.direction === "over" ? "text-emerald-400" : "text-red-400"}>
                                              {pv.direction === "over" ? "↑" : "↓"} {formatAmount(pv.diff, projectCurrency)} {pv.direction === "over" ? "больше" : "меньше"}
                                            </p>
                                            <p className="text-zinc-500 mt-0.5">
                                              Платежи: {formatAmount(pv.payments_total, projectCurrency)} / {pv.source === "requests" ? "Реквесты" : "Задачи"}: {formatAmount(pv.compare_total, projectCurrency)}
                                            </p>
                                          </div>
                                        )}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right cursor-pointer" onClick={() => navigate(projectUrl)}>
                              <span className="text-zinc-400 font-mono text-sm">{formatAmount(costs, projectCurrency)}</span>
                            </TableCell>
                            <TableCell className="text-right cursor-pointer" onClick={() => navigate(projectUrl)}>
                              {revenue > 0 ? (
                                hasCosts ? (
                                  <div className="text-right">
                                    <span className={cn("font-mono text-sm font-medium", marginColor)}>{marginPercent.toFixed(0)}%</span>
                                    <p className="text-[10px] text-zinc-500">{formatAmount(profit, projectCurrency)}</p>
                                  </div>
                                ) : (
                                  <span className="text-amber-400 text-xs font-medium">—</span>
                                )
                              ) : (
                                <span className="text-zinc-500 text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell
                              className={cn("text-right", canExpand && "cursor-pointer")}
                              onClick={() => canExpand && setBreakdownOpen(isBreakdownOpen ? null : project.id)}
                              data-testid={`net-margin-${project.id}`}
                            >
                              {hasLoaded ? (
                                <div className="flex items-center justify-end gap-1">
                                  {cb?.capped && (
                                    <span className="text-amber-400 text-xs" title="Indirect allocation capped — project sat open longer than worked (idle/waiting time not charged)">⚠</span>
                                  )}
                                  <div className="text-right">
                                    <span className={cn("font-mono text-sm font-medium", loadedColor)}>{loadedMargin.toFixed(0)}%</span>
                                    <p className="text-[10px] text-zinc-500">{formatAmount(loadedProfit, projectCurrency)}</p>
                                  </div>
                                  {canExpand && <ChevronDown className={cn("h-3 w-3 text-zinc-600 transition-transform", isBreakdownOpen && "rotate-180")} />}
                                </div>
                              ) : (
                                <span className="text-zinc-600 text-sm">—</span>
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell className="cursor-pointer" onClick={() => navigate(projectUrl)}><ChevronRight className="h-4 w-4 text-zinc-600" /></TableCell>
                      </TableRow>
                      {isBreakdownOpen && canExpand && (
                        <TableRow className="border-zinc-800 bg-zinc-950/60 hover:bg-zinc-950/60">
                          <TableCell colSpan={12} className="py-3 px-6">
                            <CostBreakdown cb={cb} />
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
            {KANBAN_COLUMNS.map((col) => {
              const cards = kanbanData[col.id] || [];
              const colRevenue = cards.reduce((s, p) => s + (p.total_revenue || p.total_amount || 0), 0);
              return (
                <div key={col.id} className={cn("flex-shrink-0 w-[220px] bg-zinc-900/50 rounded-lg border border-zinc-800 border-t-2", col.color)} data-testid={`kanban-col-${col.id}`}>
                  {/* Column header */}
                  <div className="p-2.5 border-b border-zinc-800/50">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-50 text-xs font-medium">{col.label}</span>
                      <Badge variant="outline" className="text-[10px] text-zinc-400 border-zinc-700">{cards.length}</Badge>
                    </div>
                    {!isArtDirector && colRevenue > 0 && (
                      <p className="text-emerald-400/70 text-[10px] font-mono mt-0.5">${Math.round(colRevenue).toLocaleString()}</p>
                    )}
                  </div>
                  {/* Cards */}
                  <div className="p-1.5 space-y-1.5 min-h-[80px] max-h-[calc(100vh-280px)] overflow-y-auto" id={col.id}>
                    {cards.map((project) => (
                      <KanbanCard key={project.id} project={project} navigate={navigate} getCurrencySymbol={getCurrencySymbol} hideFinancials={isArtDirector} />
                    ))}
                    {cards.length === 0 && (
                      <div className="flex items-center justify-center h-16 text-zinc-600 text-[10px]">Drop here</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </DndContext>
      )}
    </div>
  );
};

export default UnifiedProjectsPage;
